import React, { useState } from 'react';
import { Store, MapPin, Clock, FileText, CheckCircle } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const BusinessRegistration = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    businessName: '',
    businessType: '',
    email: '',
    phone: '',
    address: '',
    city: 'Guadalajara',
    state: 'Jalisco',
    postalCode: '',
    operatingHours: {
      monday: { open: '08:00', close: '20:00' },
      tuesday: { open: '08:00', close: '20:00' },
      wednesday: { open: '08:00', close: '20:00' },
      thursday: { open: '08:00', close: '20:00' },
      friday: { open: '08:00', close: '22:00' },
      saturday: { open: '08:00', close: '22:00' },
      sunday: { open: '09:00', close: '18:00' }
    },
    averageWaste: '',
    wasteTypes: []
  });

  const businessTypes = [
    'Restaurante',
    'Cafetería',
    'Panadería',
    'Supermercado',
    'Carnicería',
    'Pastelería',
    'Comida Rápida',
    'Otro'
  ];

  const wasteTypeOptions = [
    'Productos de panadería',
    'Comidas preparadas',
    'Frutas y verduras',
    'Productos lácteos',
    'Productos de carne',
    'Bebidas',
    'Productos congelados'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWasteTypeToggle = (wasteType) => {
    setFormData(prev => ({
      ...prev,
      wasteTypes: prev?.wasteTypes?.includes(wasteType)
        ? prev?.wasteTypes?.filter(type => type !== wasteType)
        : [...prev?.wasteTypes, wasteType]
    }));
  };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = () => {
    // Handle form submission
    console.log('Form submitted:', formData);
    setCurrentStep(4);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Únete a Food Loop como Negocio
          </h1>
          <p className="text-gray-600">
            Reduce desperdicios, genera ingresos adicionales y obtén certificación Zero Waste
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4]?.map((step) => (
              <div
                key={step}
                className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium ${
                  step <= currentStep
                    ? 'bg-green-600 text-white' :'bg-gray-200 text-gray-600'
                }`}
              >
                {step < currentStep ? <CheckCircle className="h-5 w-5" /> : step}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep - 1) * 33.33}%` }}
            ></div>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {currentStep === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Store className="h-6 w-6 mr-3 text-green-600" />
                Información del Negocio
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="Nombre del Negocio"
                  placeholder="Ej. Café Tapatío"
                  value={formData?.businessName}
                  onChange={(e) => handleInputChange('businessName', e?.target?.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Negocio
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={formData?.businessType}
                    onChange={(e) => handleInputChange('businessType', e?.target?.value)}
                  >
                    <option value="">Selecciona una opción</option>
                    {businessTypes?.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Correo Electrónico"
                  type="email"
                  placeholder="contacto@negocio.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                />
                <Input
                  label="Teléfono"
                  placeholder="+52 33 1234 5678"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <MapPin className="h-6 w-6 mr-3 text-green-600" />
                Ubicación y Horarios
              </h2>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="md:col-span-2">
                  <Input
                    label="Dirección"
                    placeholder="Calle, Número, Colonia"
                    value={formData?.address}
                    onChange={(e) => handleInputChange('address', e?.target?.value)}
                  />
                </div>
                <Input
                  label="Ciudad"
                  value={formData?.city}
                  onChange={(e) => handleInputChange('city', e?.target?.value)}
                />
                <Input
                  label="Estado"
                  value={formData?.state}
                  onChange={(e) => handleInputChange('state', e?.target?.value)}
                />
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-green-600" />
                  Horarios de Operación
                </h3>
                <div className="space-y-3">
                  {Object.entries(formData?.operatingHours)?.map(([day, hours]) => (
                    <div key={day} className="flex items-center gap-4">
                      <div className="w-24 text-sm font-medium text-gray-700 capitalize">
                        {day === 'monday' ? 'Lunes' :
                         day === 'tuesday' ? 'Martes' :
                         day === 'wednesday' ? 'Miércoles' :
                         day === 'thursday' ? 'Jueves' :
                         day === 'friday' ? 'Viernes' :
                         day === 'saturday' ? 'Sábado' : 'Domingo'}
                      </div>
                      <input
                        type="time"
                        className="px-3 py-1 border border-gray-300 rounded text-sm"
                        value={hours?.open}
                        onChange={(e) => handleInputChange('operatingHours', {
                          ...formData?.operatingHours,
                          [day]: { ...hours, open: e?.target?.value }
                        })}
                      />
                      <span className="text-gray-500">a</span>
                      <input
                        type="time"
                        className="px-3 py-1 border border-gray-300 rounded text-sm"
                        value={hours?.close}
                        onChange={(e) => handleInputChange('operatingHours', {
                          ...formData?.operatingHours,
                          [day]: { ...hours, close: e?.target?.value }
                        })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FileText className="h-6 w-6 mr-3 text-green-600" />
                Información sobre Desperdicios
              </h2>
              
              <div className="mb-8">
                <Input
                  label="Cantidad Promedio de Desperdicio Diario (kg)"
                  placeholder="Ej. 15"
                  value={formData?.averageWaste}
                  onChange={(e) => handleInputChange('averageWaste', e?.target?.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Tipos de Desperdicios (selecciona todos los que apliquen)
                </label>
                <div className="grid md:grid-cols-2 gap-3">
                  {wasteTypeOptions?.map(wasteType => (
                    <label
                      key={wasteType}
                      className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        checked={formData?.wasteTypes?.includes(wasteType)}
                        onChange={() => handleWasteTypeToggle(wasteType)}
                      />
                      <span className="ml-3 text-sm text-gray-700">{wasteType}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                ¡Registro Exitoso!
              </h2>
              <p className="text-gray-600 mb-8">
                Gracias por unirte a Food Loop. Nuestro equipo revisará tu solicitud y te contactará dentro de las próximas 48 horas.
              </p>
              <div className="bg-green-50 p-6 rounded-lg mb-8">
                <h3 className="font-medium text-green-900 mb-2">Próximos pasos:</h3>
                <ul className="text-sm text-green-700 space-y-1 text-left">
                  <li>• Revisaremos tu información y verificaremos tu negocio</li>
                  <li>• Te contactaremos para programar una visita inicial</li>
                  <li>• Configuraremos tu perfil en la plataforma</li>
                  <li>• Te ayudaremos a publicar tu primera bolsa sorpresa</li>
                </ul>
              </div>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => window.location.href = '/homepage'}
              >
                Volver al Inicio
              </Button>
            </div>
          )}

          {/* Navigation Buttons */}
          {currentStep < 4 && (
            <div className="flex justify-between mt-8">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
              >
                Anterior
              </Button>
              <Button
                onClick={currentStep === 3 ? handleSubmit : nextStep}
                className="bg-green-600 hover:bg-green-700"
              >
                {currentStep === 3 ? 'Enviar Solicitud' : 'Siguiente'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessRegistration;